"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";

export const GRADE_SCALE: { letter: string; points: number }[] = [
  { letter: "A+", points: 4.0 },
  { letter: "A", points: 4.0 },
  { letter: "A-", points: 3.7 },
  { letter: "B+", points: 3.3 },
  { letter: "B", points: 3.0 },
  { letter: "B-", points: 2.7 },
  { letter: "C+", points: 2.3 },
  { letter: "C", points: 2.0 },
  { letter: "C-", points: 1.7 },
  { letter: "D+", points: 1.3 },
  { letter: "D", points: 1.0 },
  { letter: "D-", points: 0.7 },
  { letter: "F", points: 0.0 },
];

const GRADE_POINTS: Record<string, number> = Object.fromEntries(
  GRADE_SCALE.map((g) => [g.letter, g.points])
);

export interface CourseRow {
  id: number;
  name: string;
  grade: string;
  credits: string;
}

let nextRowId = 0;

function makeRow(): CourseRow {
  nextRowId += 1;
  return { id: nextRowId, name: "", grade: "A", credits: "3" };
}

interface SemesterTotals {
  credits: number;
  points: number;
  gpa: number;
}

export function computeSemester(rows: CourseRow[]): SemesterTotals {
  let credits = 0;
  let points = 0;
  for (const row of rows) {
    const c = parseNumberInput(row.credits);
    const gp = GRADE_POINTS[row.grade];
    if (c !== null && c > 0 && gp !== undefined) {
      credits += c;
      points += c * gp;
    }
  }
  const gpa = credits > 0 ? points / credits : 0;
  return { credits, points, gpa };
}

function computeCumulative(
  priorGpa: string,
  priorCredits: string,
  semester: SemesterTotals
): { gpa: number; credits: number } | null {
  const gpa = parseNumberInput(priorGpa);
  const credits = parseNumberInput(priorCredits);
  if (gpa === null || credits === null || credits <= 0) {
    return null;
  }
  const totalCredits = credits + semester.credits;
  if (totalCredits <= 0) {
    return null;
  }
  const totalPoints = gpa * credits + semester.points;
  return { gpa: totalPoints / totalCredits, credits: totalCredits };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const GPA_CALCULATOR_STRINGS = {
  thisSemestersCourses: "This Semester's Courses",
  courseNameOptional: "Course name (optional)",
  coursePlaceholder: "Course {number}",
  grade: "Grade",
  credits: "Credits",
  removeCourseAria: "Remove course {number}",
  remove: "Remove",
  addCourse: "Add Course",
  semesterGpa: "Semester GPA",
  totalCredits: "Total credits",
  totalGradePoints: "Total grade points",
  cumulativeGpaOptional: "Cumulative GPA (optional)",
  cumulativeIntro:
    "Enter your GPA and completed credits from previous semesters to see how this semester changes your cumulative GPA.",
  priorGpa: "Prior GPA",
  priorGpaPlaceholder: "3.40",
  priorCredits: "Prior credits",
  priorCreditsPlaceholder: "60",
  cumulativeGpa: "Cumulative GPA",
  cumulativeCredits: "Cumulative credits",
  gradeScaleReference: "4.0 Grade Scale Reference",
  letterGrade: "Letter grade",
  gradePoints: "Grade points",
} as const;

export default function GpaCalculatorWidget() {
  const [rows, setRows] = useState<CourseRow[]>(() => [
    makeRow(),
    makeRow(),
    makeRow(),
  ]);
  const [priorGpa, setPriorGpa] = useState("");
  const [priorCredits, setPriorCredits] = useState("");

  const strings = useToolUi(GPA_CALCULATOR_STRINGS);
  const fmt = useToolNumber();

  const semester = computeSemester(rows);
  const cumulative = computeCumulative(priorGpa, priorCredits, semester);

  function updateRow(id: number, patch: Partial<CourseRow>) {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }

  function addRow() {
    setRows((prev) => [...prev, makeRow()]);
  }

  function removeRow(id: number) {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }

  return (
    <>
      {/* Course rows */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.thisSemestersCourses}
        </h2>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <div
              className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-4"
              key={row.id}
            >
              <div className="min-w-0 basis-full space-y-1 sm:flex-1 sm:basis-auto">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`course-name-${row.id}`}
                >
                  {strings.courseNameOptional}
                </label>
                <input
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`course-name-${row.id}`}
                  onChange={(e) => updateRow(row.id, { name: e.target.value })}
                  placeholder={t(strings.coursePlaceholder, {
                    number: index + 1,
                  })}
                  type="text"
                  value={row.name}
                />
              </div>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`course-grade-${row.id}`}
                >
                  {strings.grade}
                </label>
                <select
                  className="rounded-lg border border-border bg-background px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`course-grade-${row.id}`}
                  onChange={(e) => updateRow(row.id, { grade: e.target.value })}
                  value={row.grade}
                >
                  {GRADE_SCALE.map((g) => (
                    <option key={g.letter} value={g.letter}>
                      {g.letter}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`course-credits-${row.id}`}
                >
                  {strings.credits}
                </label>
                <input
                  className="w-24 rounded-lg border border-border bg-background px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`course-credits-${row.id}`}
                  min={0}
                  onChange={(e) =>
                    updateRow(row.id, { credits: e.target.value })
                  }
                  step="0.5"
                  type="number"
                  value={row.credits}
                />
              </div>
              <button
                aria-label={t(strings.removeCourseAria, { number: index + 1 })}
                className="rounded-lg border border-border bg-card px-3 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
                disabled={rows.length <= 1}
                onClick={() => removeRow(row.id)}
                type="button"
              >
                {strings.remove}
              </button>
            </div>
          ))}
        </div>
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={addRow}
          type="button"
        >
          {strings.addCourse}
        </button>
      </section>

      {/* Semester results */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard
          label={strings.semesterGpa}
          value={fmt(semester.gpa, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        />
        <StatCard label={strings.totalCredits} value={semester.credits} />
        <StatCard
          label={strings.totalGradePoints}
          value={fmt(semester.points, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}
        />
      </div>

      {/* Cumulative section */}
      <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.cumulativeGpaOptional}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.cumulativeIntro}
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-xs"
              htmlFor="prior-gpa"
            >
              {strings.priorGpa}
            </label>
            <input
              className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="prior-gpa"
              max={4}
              min={0}
              onChange={(e) => setPriorGpa(e.target.value)}
              placeholder={strings.priorGpaPlaceholder}
              step="0.01"
              type="number"
              value={priorGpa}
            />
          </div>
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-xs"
              htmlFor="prior-credits"
            >
              {strings.priorCredits}
            </label>
            <input
              className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="prior-credits"
              min={0}
              onChange={(e) => setPriorCredits(e.target.value)}
              placeholder={strings.priorCreditsPlaceholder}
              step="0.5"
              type="number"
              value={priorCredits}
            />
          </div>
        </div>
        {cumulative && (
          <div className="grid grid-cols-2 gap-4">
            <StatCard
              label={strings.cumulativeGpa}
              value={fmt(cumulative.gpa, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            />
            <StatCard
              label={strings.cumulativeCredits}
              value={cumulative.credits}
            />
          </div>
        )}
      </section>

      {/* Grade scale reference */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.gradeScaleReference}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border border-b">
                <th className="py-2 pr-4 font-medium text-foreground">
                  {strings.letterGrade}
                </th>
                <th className="py-2 font-medium text-foreground">
                  {strings.gradePoints}
                </th>
              </tr>
            </thead>
            <tbody className="text-muted-foreground">
              {GRADE_SCALE.map((g) => (
                <tr className="border-border/50 border-b" key={g.letter}>
                  <td className="py-2 pr-4 font-medium text-foreground">
                    {g.letter}
                  </td>
                  <td className="py-2">
                    {fmt(g.points, {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
